import _ from 'underscore';
import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Dialog, DialogType } from '@fluentui/react/lib/Dialog';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { TextField } from '@fluentui/react/lib/TextField';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  useClientContext,
  EditorStatusContext,
  PreviewMode,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  validateEmailAddress,
  closeIcon,
} from '@bingads-webui-campaign-react/smart-component-util';

const getStyles = () => ({
  close: {
    background: 'none',
    border: 'none',
    outline: 'none',
    position: 'absolute',
    right: 20,
    width: 20,
    height: 20,
    padding: 0,
    lineHeight: '10px',
    '&:focus': {
      border: '1px solid',
    },
  },
  icon: {
    color: '#323130',
    width: 12,
    height: 12,
  },
  group: {
    margin: '10px auto',
    minHeight: 85,
  },
  errorGroup: {
    minHeight: 40,
    margin: '0px auto 10px auto',
  },
  groupWidth: {
    width: 380,
  },
  mobileGroupWidth: {
    width: 230,
  },
  actionGroup: {
    textAlign: 'center',
    margin: '0px 30px 30px 30px',
  },
  submit: {
    borderRadius: 100,
    height: 48,
    padding: '15px 34px 15px 34px',
    fontSize: 20,
    lineHeight: '24px',
    backgroundColor: 'black',
    border: '1px solid black',
    '&:hover': {
      backgroundColor: 'black',
      border: '1px solid black',
    },
    '&[disabled]': {
      backgroundColor: 'lightgray',
      border: '1px solid lightgray',
      color: 'darkgray',
    },
  },
});

const dialogStyles = { main: { maxWidth: 450 } };

const dialogContentStyle = {
  title: {
    fontSize: 24,
    padding: 20,
    lineHeight: '28px',
    textAlign: 'center',
  },
};

export const UnstyledEmailForm = ({
  classes,
  title,
  hideDialog,
  showError,
  serverError,
  showWarning,
  onSubmit,
  onCancel,
  i18n,
  requestSent,
}) => {
  const phoneRegEx = /^[+\-\s.()/0-9]+$/; // a loose check that allows number, space +, -, (, ), .

  const [name, setName] = useState('');
  const [phone, setPhone] = useState(i18n.getString(_TL_('')));
  const [mail, setMail] = useState('');
  const [message, setMessage] = useState('');
  const [nameError, setNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [mailError, setMailError] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isMailValid, setIsMailValid] = useState(false);

  const { isMobile } = useClientContext();
  const editorStatusContext = useContext(EditorStatusContext);
  let isEditorMobileMode = false;

  if (_.isArray(editorStatusContext) && editorStatusContext.length) {
    const editorStatus = editorStatusContext[0];
    isEditorMobileMode = editorStatus && editorStatus.previewMode === PreviewMode.Mobile;
  }

  const showMobileForm = isMobile || isEditorMobileMode;
  const groupWidth = showMobileForm ? classes.mobileGroupWidth : classes.groupWidth;

  const onChangeName = useCallback((event, newValue) => {
    setName(newValue || '');
    if (_.isEmpty(newValue)) {
      setIsNameValid(false);
      setNameError(i18n.getString(_TL_('Required.')));
      return;
    }
    setIsNameValid(true);
    setNameError('');
  }, [i18n]);

  const onChangePhone = useCallback((event, newValue) => {
    setPhone(newValue || '');
    if (phoneRegEx.test(newValue)) {
      setIsPhoneValid(true);
      setPhoneError('');
      return;
    }
    setIsPhoneValid(false);
    setPhoneError(i18n.getString(_TL_('Invalid phone number')));
  }, [i18n, phoneRegEx]);

  const onChangeMail = useCallback((event, newValue) => {
    setMail(newValue || '');
    if (validateEmailAddress(newValue)) {
      setIsMailValid(true);
      setMailError('');
      return;
    }
    setIsMailValid(false);
    setMailError(i18n.getString(_TL_('Invalid email address')));
  }, [i18n]);

  const onChangeMessage = useCallback((event, newValue) => {
    setMessage(newValue || '');
  }, []);

  const onSubmitClick = () => {
    onSubmit({
      name,
      phone,
      mail,
      message,
    });
  };

  const requestFailMessage = (
    <MessageBar
      className="request-error-message"
      messageBarType={MessageBarType.error}
    >
      <span>
        {i18n.getString(_TL_('Fail to send mail.'))}
      </span>
      <span className="error-detail">
        {serverError}
      </span>
    </MessageBar>
  );

  const previewWarningMessage = (
    <MessageBar
      className="preview-warning-message"
      isMultiline
      messageBarType={MessageBarType.warning}
    >
      <span>
        {i18n.getString(_TL_('Email functionality is not supported in Preview mode. To send emails, publish your website and try again.'))}
      </span>
    </MessageBar>
  );

  const header = (
    <div>
      <span className={classes.title}>{title}</span>
      <button
        className={classNames('form-cancel-button', classes.close)}
        onClick={onCancel}
        aria-label={i18n.getString(_TL_('Close'))}
      >
        <img className={classes.icon} src={closeIcon} alt="" />
      </button>
    </div>
  );

  return (
    <Dialog
      hidden={hideDialog}
      onDismiss={onCancel}
      maxWidth="fit-content"
      dialogContentProps={{
        type: DialogType.normal,
        title: header,
        styles: dialogContentStyle,
        showCloseButton: false,
      }}
      modalProps={{
        isBlocking: true,
        styles: dialogStyles,
        className: classNames('email-form', classes.dialog),
      }}
    >
      <div className={classNames(classes.group, groupWidth)}>
        <TextField
          className="input-name"
          label={i18n.getString(_TL_('Name'))}
          placeholder={i18n.getString(_TL_('your name'))}
          value={name}
          onChange={onChangeName}
          errorMessage={nameError}
        />
      </div>
      <div className={classNames(classes.group, groupWidth)}>
        <TextField
          className="input-phone-number"
          label={i18n.getString(_TL_('Phone number'))}
          value={phone}
          onChange={onChangePhone}
          errorMessage={phoneError}
        />
      </div>
      <div className={classNames(classes.group, groupWidth)}>
        <TextField
          className="input-email"
          label={i18n.getString(_TL_('Email address'))}
          placeholder={i18n.getString(_TL_('your email address'))}
          value={mail}
          onChange={onChangeMail}
          errorMessage={mailError}
        />
      </div>
      <div className={classNames(classes.group, groupWidth)}>
        <TextField
          className="input-message"
          label={i18n.getString(_TL_('Message'))}
          placeholder={i18n.getString(_TL_('I need help with the following:'))}
          value={message}
          onChange={onChangeMessage}
          multiline
          resizable={false}
        />
      </div>
      <div className={classNames(classes.errorGroup, groupWidth)}>
        {
          showError && requestFailMessage
        }
        {
          showWarning && previewWarningMessage
        }
      </div>
      <div className={classes.actionGroup}>
        <PrimaryButton
          className={classNames('form-submit-button', classes.submit)}
          onClick={onSubmitClick}
          text={i18n.getString(_TL_('Submit'))}
          disabled={requestSent || showWarning || !isNameValid || !isPhoneValid || !isMailValid}
        />
      </div>
    </Dialog>
  );
};

UnstyledEmailForm.propTypes = {
  classes: PropTypes.shape({
    dialog: PropTypes.string,
    close: PropTypes.string,
    icon: PropTypes.string,
    group: PropTypes.string,
    submit: PropTypes.string,
  }).isRequired,
  title: PropTypes.string.isRequired,
  hideDialog: PropTypes.bool.isRequired,
  showError: PropTypes.bool.isRequired,
  serverError: PropTypes.string,
  showWarning: PropTypes.bool,
  requestSent: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

UnstyledEmailForm.defaultProps = {
  serverError: '',
  showWarning: false,
  requestSent: false,
};

export const EmailForm = withDefaultStyles(
  UnstyledEmailForm,
  getStyles
);
